import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { EditableCellTableColumnProps } from "Components/General/EditableCellTable";
import { FormatData } from "utils/FormatData";
import { stringSort } from "utils/defaultSort";
import { ResolveException } from "./ResolveException";
import { Row, Tag, Popover, Skeleton, Tooltip, Tabs, TableProps, Table, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FileUploadActions, ActionCommand } from "./FileUploadActions";
import { EditableCellTable } from "Components/General/EditableCellTable";
import { fileUploadStore, UploadedTmpFile } from "./FileUploadStore";
import { ReplaceFilePicker } from "./ReplaceFilePicker";
import { UploadErrorStatus } from "./UploadErrorSatus";
import { EllipsisLeft } from "Components/General/EllipsisLeft";
import { Loader } from "Components/General/Loader";
import { FilenameAssistantOverview } from "../FilenameAssistant/FilenameAssistantOverview";
import { filenameAssistantStore } from "../FilenameAssistant/FilenameAssistantStore";
import { MetadataAssistantModal } from "../MetadataAssistant/MetadataAssistantModal";
import { metadataAssistantStore } from "../MetadataAssistant/MetadataAssistantStore";
import { projectsDataStore } from "Stores";
import { PathDirectorySelectPlaceholder } from "Pages/Project/Documents/FileUpload/PathDirectorySelectPlaceholder";
import { Button } from "Components/General/Button";
import { PaginationProps } from "antd/lib/pagination";
import { ColumnFilterItem } from "antd/lib/table/interface";
import { DetailListActions, DropdownActionConfig } from "Components/General/DetailListActions";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { PathDirectorySelect } from "Components/Form/PathDirectorySelect";
import { replaceFileStore } from "./ReplaceFileStore";
import { ChangeFileDescription } from "./ChangeFileDescription";
import { modal } from "Components/General/Modal/AntdModal";
import { hasBlacklistedFilenameChars, getBlacklistedFilenameCharsMessage } from "utils/inputValidation";
import Text from "antd/lib/typography/Text";

interface ValidatedFilenameInputProps {
  defaultValue: string;
  onChange: (value: string) => void;
  suffix: string;
  placeholder: string;
  validationRef: React.MutableRefObject<boolean>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const ValidatedFilenameInput: React.FC<ValidatedFilenameInputProps> = ({
  defaultValue,
  onChange,
  suffix,
  placeholder,
  validationRef,
  t,
}) => {
  const [error, setError] = React.useState(() => {
    if (hasBlacklistedFilenameChars(defaultValue)) {
      validationRef.current = false;
      return getBlacklistedFilenameCharsMessage(t);
    }
    return "";
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(value);
    if (hasBlacklistedFilenameChars(value)) {
      setError(getBlacklistedFilenameCharsMessage(t));
      validationRef.current = false;
    } else {
      setError("");
      validationRef.current = true;
    }
  };

  return (
    <>
      <Input
        placeholder={placeholder}
        onChange={handleChange}
        autoFocus={true}
        defaultValue={defaultValue}
        addonAfter={suffix}
      />
      {error && (
        <Text type="danger" style={{ display: "block", marginTop: 4 }}>
          {error}
        </Text>
      )}
    </>
  );
};

const GreenTag = ({ children }) =>
  children ? (
    <Tag color="#d7f9b0" variant="solid" style={{ border: "1px solid #9cd956" }}>
      <span className="color-black">{children}</span>
    </Tag>
  ) : null;

function getFiltersForErrors(data: API_UploadFile[]): EditableCellTableColumnProps<UploadedTmpFile>["filters"] {
  const filterErrorsMap = data.reduce<{ [key in API_UPLOADERRORINFO_STATUS]?: ColumnFilterItem }>((buffer, current) => {
    const errorStatus = current.errors?.status;
    if (buffer[errorStatus]) {
      return buffer;
    }
    buffer[errorStatus] = { value: errorStatus, text: <UploadErrorStatus status={errorStatus} /> };
    return buffer;
  }, {});
  return Object.values(filterErrorsMap);
}
interface FileSubmitterProps extends WithTranslation, WithModal {
  onBack: () => void;
  onSubmit: () => Promise<void>;
}

interface FileSubmitterState {
  validFiles: API_UploadFile[];
  erroredFiles: UploadedTmpFile[];
  selectedErroredFileKeys: string[];
  selectedValidFileKeys: string[];
  retryLoading: boolean;
  submitLoading: boolean;
  initialized: boolean;
  nameChanged: number[];
  directoryChanged: number[];
  mdChanged: number[];
  efPagination: PaginationProps;
  vfPagination: PaginationProps;
  oldFileIdChanged: { name: string; id: number }[];
  singleFile: {
    newFolderId: number;
    newFolderDirname: string;
    newFilename: string;
    fileIdToReplace: number;
  };
  multiFiles: {
    newFolderId: number;
  };
}

export const FileSubmitter = withTranslation(I18N_NS.PROJECT)(
  withModal(
    observer(
      class extends React.Component<FileSubmitterProps, FileSubmitterState> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private erroredFilesRef: React.RefObject<any>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        private validFilesRef: React.RefObject<any>;
        private readonly filenameValidRef: React.MutableRefObject<boolean> = { current: true };
        constructor(props: FileSubmitterProps) {
          super(props);

          this.state = {
            validFiles: [],
            erroredFiles: [],
            selectedErroredFileKeys: [],
            selectedValidFileKeys: [],
            retryLoading: false,
            submitLoading: false,
            initialized: false,
            nameChanged: [],
            directoryChanged: [],
            mdChanged: [],
            oldFileIdChanged: [],
            efPagination: {
              pageSize: 10,
              current: 1,
            },
            vfPagination: {
              pageSize: 10,
              current: 1,
            },
            singleFile: {
              newFolderId: null,
              newFolderDirname: null,
              newFilename: null,
              fileIdToReplace: null,
            },
            multiFiles: {
              newFolderId: null,
            },
          };
          this.erroredFilesRef = React.createRef();
          this.validFilesRef = React.createRef();
        }

        componentDidMount() {
          (async () => {
            try {
              if (fileUploadStore.module === "MAIL") {
                await fileUploadStore.checkMailAttachs(true);
              } else {
                await fileUploadStore.checkUpload();
              }
              if (projectsDataStore.currentProject.functions.includes("METADATA")) {
                await metadataAssistantStore.fetch();
              }
              this.parseFiles();
            } catch (error) {
              throw error;
            } finally {
              this.setState({ initialized: true });
            }
          })();
        }

        parseFiles = (cb?: () => void) => {
          const { erroredFiles, validFiles } = fileUploadStore.result.files.reduce(
            (buffer, current) => {
              if (current.errors) {
                buffer.erroredFiles.push(current);
              } else {
                buffer.validFiles.push(current);
              }
              return buffer;
            },
            { validFiles: [], erroredFiles: [] }
          );

          /*
           * NOTE: hack! We make table re-render by setting empty arrays and then returning saved values.
           */
          this.setState({ erroredFiles: [], validFiles: [] }, () => {
            this.setState({ erroredFiles, validFiles }, () => {
              this.defineMissingMDIds([]);
              if (cb) {
                cb();
              }
            });
          });

          this.handleFilenameErrors(erroredFiles);
        };

        handleFilenameErrors = (data: API_UploadFile[]) => {
          const files = data.filter((file) => file.errors.status === "FILENAMECONVENTION_FILENAME");
          filenameAssistantStore.setFiles(files);
        };

        defineMissingMDIds = (files?: API_UploadFile[]) => {
          const mdFiles = [];
          files.forEach((file) => {
            file.errors.missingMetadata.forEach((md) => {
              mdFiles.push(md);
            });
          });
          metadataAssistantStore.setMissingMetadata(mdFiles);
        };

        handleMetadataEdit = (mds: API_UploadMetadata[], files: API_UploadFile[], multiple?) => {
          const erroredFilesBuffer = this.state.erroredFiles.slice();
          const mdChangedBuffer = this.state.mdChanged.slice();

          // for each file replaces metadata with updated values
          const editMD = (file: UploadedTmpFile, metadata: API_UploadMetadata[]) => {
            const updatedFile = { ...file, metadata: multiple ? file.errors.missingMetadata : metadata };
            fileUploadStore.editFile(updatedFile);
            const erroredFilesIdx = erroredFilesBuffer.findIndex((item) => item.id === file.id);
            if (erroredFilesIdx !== -1) {
              erroredFilesBuffer[erroredFilesIdx] = updatedFile;
            }
            const mdChangedIdx = mdChangedBuffer.findIndex((item) => item === file.id);
            if (mdChangedIdx === -1) {
              mdChangedBuffer.push(file.id);
            }
          };

          files.forEach((file) => {
            editMD(file as UploadedTmpFile, mds);
          });
          this.setState({ erroredFiles: erroredFilesBuffer, mdChanged: mdChangedBuffer });
          metadataAssistantStore.setActiveFiles([]);
        };

        /**
         * @param multipleFiles - if true, will not run check for each file separately
         */
        handleEdit = (value: API_UploadFile, multipleFiles: boolean = false) => {
          return new Promise((resolve) => {
            fileUploadStore.editFile(value);
            this.setState(
              (prevState) => {
                const erroredFileIdx = prevState.erroredFiles.findIndex((item) => item.id === value.id);
                const validFileIdx = prevState.validFiles.findIndex((item) => item.id === value.id);
                const erroredFileFound = erroredFileIdx !== -1;
                const validFileFound = validFileIdx !== -1;
                // if file match was found
                if (erroredFileFound || validFileFound) {
                  let nameChangedId: number;
                  let oldFileIdChangedId: number;
                  let idx: number;
                  let namespace: keyof Pick<FileSubmitterState, "erroredFiles" | "validFiles">;
                  if (erroredFileFound) {
                    namespace = "erroredFiles";
                    idx = erroredFileIdx;
                  } else {
                    idx = validFileIdx;
                    namespace = "validFiles";
                  }
                  // if name differs from previous
                  if (value.name !== prevState[namespace][idx].name) {
                    nameChangedId = value.id;
                  }
                  // if directory differs from previous
                  if (value.dirid !== prevState[namespace][idx].dirid && !multipleFiles) {
                    prevState.directoryChanged.push(value.id);
                    this.handleCheck();
                  }

                  // if file that is going to be replaced differs from previous
                  if (prevState[namespace][idx].oldFile?.id !== value.oldFile?.id) {
                    oldFileIdChangedId = value.oldFile?.id;
                  }
                  // push file id's into state
                  if (nameChangedId) {
                    prevState.nameChanged.push(nameChangedId);
                  }
                  if (oldFileIdChangedId) {
                    prevState.oldFileIdChanged.push({
                      name: value.errors?.existingFile ? value.errors.existingFile.name : undefined,
                      id: value.id,
                    });
                  }
                }

                if (erroredFileFound) {
                  prevState.erroredFiles[erroredFileIdx] = value as UploadedTmpFile;
                } else if (validFileFound) {
                  prevState.validFiles[validFileIdx] = value;
                }
                return prevState;
              },
              () => {
                /* NOTE: it's a hack! Because when you change filename not within EditableCellTable - changes are not
          propagated to EditableCell. We make it re-render by setting empty arrays and then returning saved values.
          */
                const validFilesBuffer = [...this.state.validFiles];
                const erroredFilesBuffer = [...this.state.erroredFiles];
                this.setState(
                  {
                    validFiles: [],
                    erroredFiles: [],
                  },
                  () => {
                    this.setState({
                      validFiles: validFilesBuffer,
                      erroredFiles: erroredFilesBuffer,
                    });
                  }
                );
                resolve(undefined);
              }
            );
          });
        };

        handleDelete = (ids: string | number | string[] | number[]) => {
          let fileIds: string[] | number[];
          if (Array.isArray(ids)) {
            fileIds = ids;
          } else {
            fileIds = [ids as number];
          }
          const filterFunction = (key) => fileIds.some((fileId) => fileId.toString() === key.toString());
          // remove selected keys for removed files
          const selectedErroredFileKeys = this.state.selectedErroredFileKeys.filter(filterFunction);
          const selectedValidFileKeys = this.state.selectedValidFileKeys.filter(filterFunction);
          this.setState({
            selectedErroredFileKeys,
            selectedValidFileKeys,
          });
          fileIds.forEach((id) => fileUploadStore.deleteFile(id));
          this.parseFiles();
        };

        handleSubmit = async () => {
          try {
            this.setState({ submitLoading: true });
            await this.props.onSubmit();
            this.parseFiles();
          } catch (error) {
            throw error;
          } finally {
            this.setState({ submitLoading: false });
          }
        };

        handleCheck = async (all: boolean = false) => {
          try {
            this.setState({ retryLoading: true });
            if (fileUploadStore.module === "MAIL") {
              await fileUploadStore.checkMailAttachs();
            } else {
              await fileUploadStore.checkUpload();
            }
            this.parseFiles();
            const newState: Partial<FileSubmitterState> = {
              oldFileIdChanged: [],
              nameChanged: [],
              mdChanged: [],
              selectedErroredFileKeys: [],
              selectedValidFileKeys: [],
              directoryChanged: this.state.directoryChanged,
            };
            if (all) {
              newState.directoryChanged = [];
            }
            this.setState(newState as FileSubmitterState);
          } catch (error) {
            throw error;
          } finally {
            this.setState({ retryLoading: false });
          }
        };

        handleMetadataModalOpen = (files: API_UploadFile[]) => {
          this.defineMissingMDIds(files);
          metadataAssistantStore.setActiveFiles(files);
          metadataAssistantStore.toggleModalVisible();
        };
        handleMetadataAutofill = (files: API_UploadFile[], multiple?: boolean) => {
          this.defineMissingMDIds(files);
          metadataAssistantStore.setActiveFiles(files);
          this.handleMetadataEdit(metadataAssistantStore.missingMetadata, files, multiple);
        };

        /* Need to distinguish between single and multiple file actions to trigger "handleCheck" once */
        applyFilenameRulesForFiles = async (files: UploadedTmpFile[]) => {
          for (const file of files) {
            await this.handleEdit(file);
          }
          this.handleCheck();
        };

        handleFilenameAssistantOpen = (value: UploadedTmpFile) => {
          return this.state.erroredFiles.find(
            (file) => file.errors.status === "FILENAMECONVENTION_FILENAME" && file.id === value.id
          );
        };

        setFolderId = (newFolderId: number, file?: UploadedTmpFile) => {
          this.handleEdit({
            ...file,
            dirid: newFolderId,
            name: file.name,
            dirname: file.dirname,
            oldFile: file.oldFile,
          });
        };

        replaceExistingFiles = (files: API_UploadFile[]) => {
          files.forEach((file) => {
            this.handleEdit({
              ...file,
              oldFile: { id: file.errors?.existingFile?.id },
            });
          });
        };

        renderDirectoryColumn = (text, record: UploadedTmpFile) => {
          const { t } = this.props;
          const parsedPath = record.archivePath || text;
          const path = parsedPath ? "/" + parsedPath.replace(/\\/g, "/") : "/";

          if (record.changeTargetDir) {
            const pathSelector = (
              <PathDirectorySelectPlaceholder
                onChangeForFile={this.setFolderId}
                file={record}
                value={record.dirid}
                treeSelectProps={{
                  style: { width: "100%" },
                  placeholder: path,
                }}
              >
                <EllipsisLeft text={path} color={this.state.directoryChanged.includes(record.id) ? "blue" : "black"} />
              </PathDirectorySelectPlaceholder>
            );

            return (
              <Tooltip title={record.directoryWarning || t("Change target directory")}>
                {record.directoryWarning ? (
                  <Tag color={"#ff8145"} variant="solid" style={{ width: "98%" }}>
                    {pathSelector}
                  </Tag>
                ) : (
                  <div>{pathSelector}</div>
                )}
              </Tooltip>
            );
          } else {
            return (
              <Tooltip
                title={
                  record.directoryWarning ||
                  t("Automatic Directory Mapping active. You can’t change the target directory")
                }
              >
                <Tag
                  color={record.directoryWarning ? "#3465A4" : "#d7f9b0"} // blue or green
                  variant="solid"
                  style={{ width: "98%", border: "1px solid " + (record.directoryWarning ? "#365a8a" : "#9cd956") }}
                >
                  <EllipsisLeft color={record.directoryWarning ? "white" : "black"} text={path} />
                </Tag>
              </Tooltip>
            );
          }
        };

        getDefaultFileName = (file: UploadedTmpFile) => {
          const { name, type } = file;
          const cutIndex = name.indexOf(`.${type}`);
          let newFilename = "";
          if (cutIndex !== -1) {
            newFilename = name.slice(0, cutIndex);
          }
          return newFilename;
        };

        handleFolderIdChange = async (newFolderId: number) => {
          try {
            this.setState((state) => {
              const newState = { ...state };
              newState.singleFile.newFolderId = newFolderId;
              newState.multiFiles.newFolderId = newFolderId;
              return newState;
            });
          } catch (error) {
            throw error;
          }
        };

        setFilename = (value) => {
          this.setState((state) => {
            const newState = { ...state };
            newState.singleFile.newFilename = value;
            return newState;
          });
        };

        resetFilename = (file: UploadedTmpFile) => {
          return new Promise<void>((resolve) => {
            this.setState((state) => {
              const newState = { ...state };
              newState.singleFile.newFilename = this.getDefaultFileName(file);
              return newState;
            }, resolve);
          });
        };

        setExistingFileIdToReplace = (file: UploadedTmpFile) => {
          const fileIdToReplace = file.errors?.existingFile?.id;
          this.setState(
            (state) => {
              const newState = { ...state };
              newState.singleFile.fileIdToReplace = fileIdToReplace;
              return newState;
            },
            () => this.submitChanges(file)
          );
        };

        submitChanges = (file: UploadedTmpFile, isRename: boolean = false) => {
          const { newFilename, newFolderDirname, newFolderId, fileIdToReplace } = this.state.singleFile;
          this.handleEdit({
            ...file,
            index: file.index,
            dirid: isRename ? file.dirid : newFolderId,
            dirname: isRename ? file.dirname : newFolderDirname,
            name: `${newFilename}.${file.type}`,
            oldFile: { id: fileIdToReplace },
          });
          this.setState((state) => {
            const newState = { ...state };
            Object.keys(newState.singleFile).forEach((key) => (newState.singleFile[key] = null));
            Object.keys(newState.multiFiles).forEach((key) => (newState.multiFiles[key] = null));
            return newState;
          });
        };

        handleMultiChangeDir = (files: UploadedTmpFile[]) => {
          (async () => {
            files.forEach((file) => {
              const dirid = this.state.multiFiles.newFolderId || fileUploadStore.folderId;
              this.handleEdit(
                {
                  ...file,
                  dirid,
                },
                true
              );
            });
          })();
          this.handleCheck();
        };

        handleMultiChangeDescription = (files: API_UploadFile[], description: string) => {
          (async () => {
            files.forEach((file) => {
              this.handleEdit(
                {
                  ...file,
                  description,
                },
                true
              );
            });
          })();
          this.handleCheck();
        };

        handleSingleActionCommand = async (command: ActionCommand, file: UploadedTmpFile) => {
          const { t } = this.props;
          await new Promise<void>((res) => {
            this.setState((state) => {
              const newState = { ...state };
              newState.singleFile.newFolderId = file.dirid;
              newState.singleFile.newFolderDirname = file.dirname;
              newState.singleFile.newFilename = this.getDefaultFileName(file);
              newState.singleFile.fileIdToReplace = file.oldFile?.id;
              return newState;
            }, res);
          });

          if (command === "rename") {
            this.filenameValidRef.current = true;
            modal.confirm({
              title: t("Change filename"),
              width: 760,
              icon: null,
              content: (
                <ValidatedFilenameInput
                  placeholder={t("Filename")}
                  onChange={(value) => this.setFilename(value)}
                  defaultValue={this.state.singleFile.newFilename}
                  suffix={file.type}
                  validationRef={this.filenameValidRef}
                  t={t}
                />
              ),
              onOk: () => {
                if (!this.filenameValidRef.current) {
                  return Promise.reject(new Error("Filename validation failed"));
                }
                return this.submitChanges(file, true);
              },
              onCancel: this.resetFilename,
            });
          } else if (command === "change_path") {
            modal.confirm({
              title: t("Change target directory"),
              content: (
                <PathDirectorySelect
                  onChange={this.handleFolderIdChange}
                  value={this.state.singleFile.newFolderId}
                  treeSelectProps={{
                    style: { width: "100%" },
                    placeholder: t("File path"),
                  }}
                />
              ),
              onOk: () => this.submitChanges(file),
            });
          } else if (command === "replace_existing") {
            this.setExistingFileIdToReplace(file);
          } else if (command === "replace_other") {
            replaceFileStore.initModal(file, true);
          } else if (command === "remove") {
            this.handleDelete(file.id);
          } else if (command === "filename_assistant" && projectsDataStore.currentProject.fileNameAssistant) {
            this.props.showModal(FilenameAssistantOverview, {
              modalProps: {
                footer: null,
                title: t("Filename assistant"),
                width: "100%",
                className: "full-height-modal",
                style: { top: 30, maxWidth: `calc(100vw - 80px)` },
                maskClosable: true,
              },
              componentProps: {
                file: this.handleFilenameAssistantOpen(file),
                onSubmit: this.applyFilenameRulesForFiles,
              },
            });
          } else if (command === "metadata") {
            this.handleMetadataModalOpen([file]);
          }
        };

        getViewsColumn<T extends API_UploadFile>(): EditableCellTableColumnProps<T> {
          const { t } = this.props;
          return {
            title: t("Views"),
            dataIndex: "views",
            ellipsis: {
              showTitle: false,
            },
            render: (value: string[]) => {
              return value && value.length ? (
                <>
                  {value.join(", ")}
                  <Popover
                    trigger="click"
                    content={value.map((v, i) => (
                      <div key={`${v}_${i}`}>{v}</div>
                    ))}
                    placement="bottomLeft"
                  >
                    <div className="views-list-expand">
                      <div className="views-list-expand-icon pointer">
                        <span className="mr-sm">{t("Expand")}</span>
                        <FontAwesomeIcon fixedWidth={true} icon={["far", "expand-alt"]} color="#000000" />
                      </div>
                    </div>
                  </Popover>
                </>
              ) : null;
            },
          };
        }

        getActionsColumn<T extends API_UploadFile>(): EditableCellTableColumnProps<T> {
          return {
            title: this.props.t("Actions"),
            dataIndex: "actions",
            editable: false,
            render: (_text, record) => (
              <Row justify="center">
                <FileUploadActions
                  onSelectFileAction={this.handleSingleActionCommand}
                  file={record}
                  module={fileUploadStore.module}
                />
              </Row>
            ),
            width: 80,
          };
        }

        getCommonColumns<T extends API_UploadFile>(): EditableCellTableColumnProps<T>[] {
          const { t } = this.props;
          const columns: EditableCellTableColumnProps<T>[] = [
            {
              title: t("Description"),
              dataIndex: "description",
              editable: true,
              sorter: (a, b) => (a.description ?? "").localeCompare(b.description ?? ""),
            },
          ];
          if (fileUploadStore.module === "DOCUMENTS" || fileUploadStore.module === "MAIL") {
            columns.push(
              {
                title: t("Index"),
                dataIndex: "index",
                editable: fileUploadStore.indexCanBeEdited,
                sorter: (a, b) => stringSort(a, b, "index"),
                render: (text) => (fileUploadStore.indexCanBeEdited ? text : <GreenTag>{text}</GreenTag>),
              },
              {
                title: t("File path"),
                dataIndex: "dirname",
                render: this.renderDirectoryColumn as unknown as EditableCellTableColumnProps<T>["render"],
                width: 400,
              }
            );
          }
          if (fileUploadStore.module === "VIEW") {
            columns.push(this.getViewsColumn());
          }
          columns.push({
            title: t("Size"),
            dataIndex: "size",
            width: 120,
            render: (text) => FormatData.formatBytes(+text),
          });
          return columns;
        }

        get erroredFilesColumns(): EditableCellTableColumnProps<UploadedTmpFile>[] {
          const { t } = this.props;
          const columns: EditableCellTableColumnProps<UploadedTmpFile>[] = [
            {
              title: t("Filename"),
              dataIndex: "name",
              defaultSortOrder: "ascend",
              sorter: (a, b) => stringSort(a, b, "name"),
              onCell: (record) => ({
                className: this.state.nameChanged.includes(record.id) ? "color-blue" : "",
              }),
              render: (text, record) => {
                return record?.errors?.status === "RENAMEFILE" ? (
                  <div
                    onClick={async () => {
                      await new Promise<void>((res) => {
                        this.setState((state) => {
                          const newState = { ...state };
                          newState.singleFile.newFolderId = record.dirid;
                          newState.singleFile.newFolderDirname = record.dirname;
                          newState.singleFile.newFilename = this.getDefaultFileName(record);
                          newState.singleFile.fileIdToReplace = record.oldFile.id;
                          return newState;
                        }, res);
                      });
                      this.filenameValidRef.current = true;
                      modal.confirm({
                        title: t("Change filename"),
                        width: 760,
                        icon: null,
                        content: (
                          <ValidatedFilenameInput
                            placeholder={t("Filename")}
                            onChange={(value) => this.setFilename(value)}
                            defaultValue={this.state.singleFile.newFilename}
                            suffix={record.type}
                            validationRef={this.filenameValidRef}
                            t={t}
                          />
                        ),
                        onOk: () => {
                          if (!this.filenameValidRef.current) {
                            return Promise.reject(new Error("Filename validation failed"));
                          }
                          return this.submitChanges(record, true);
                        },
                        onCancel: this.resetFilename,
                      });
                    }}
                  >
                    {text}
                  </div>
                ) : (
                  text
                );
              },
            },
            ...this.getCommonColumns<UploadedTmpFile>(),
          ];
          columns.push(
            {
              title: t("Notes"),
              dataIndex: "",
              width: 150,
              filterMultiple: false,
              filters: getFiltersForErrors(this.state.erroredFiles),
              onFilter: (filterValue, record) => Boolean(record.errors?.status === filterValue),
              render: (text, file) => {
                if (file.errors) {
                  // DEV-3298
                  const foundOldFile = this.state.oldFileIdChanged.find((item) => item.id === file.id);
                  const foundOldMD = this.state.mdChanged.find((fid) => fid === file.id);
                  let replacesFileTag: React.ReactNode = <span />;
                  // adds "replaces file" tag if old file id was changed
                  if (foundOldFile) {
                    replacesFileTag = (
                      <div>
                        <Popover
                          content={foundOldFile.name}
                          style={{ maxWidth: 400 }}
                          trigger={"click"}
                          placement="left"
                        >
                          <Tag color="#3465A4" variant="solid">
                            {t("Replaces file")}
                          </Tag>
                        </Popover>
                      </div>
                    );
                  }
                  if (foundOldMD) {
                    replacesFileTag = (
                      <div>
                        <Tag color="#3465A4" variant="solid">
                          {t("Added meta data")}
                        </Tag>
                      </div>
                    );
                  }

                  return (
                    <>
                      {replacesFileTag}
                      <ResolveException
                        errors={file.errors}
                        filenamePattern={file.errors.messages?.[0]}
                        fileName={file.name}
                        messages={file.errors.messages}
                        getFileById={fileUploadStore.getFileById}
                      >
                        <UploadErrorStatus
                          status={file.errors.status}
                          onStatusClick={this.handleSingleActionCommand}
                          file={file}
                        />
                      </ResolveException>
                    </>
                  );
                } else {
                  return null;
                }
              },
            },
            this.getActionsColumn<UploadedTmpFile>()
          );
          return columns;
        }

        get validFilesColumns(): EditableCellTableColumnProps<API_UploadFile>[] {
          const { t } = this.props;
          const columns: EditableCellTableColumnProps<API_UploadFile>[] = [
            {
              title: t("Filename"),
              dataIndex: "name",
              defaultSortOrder: "ascend",
              sorter: (a, b) => stringSort(a, b, "name"),
              render: (text, record) => (record.filenameChange ? <GreenTag>{text}</GreenTag> : text),
              onCell: (record) => ({
                className: this.state.nameChanged.includes(record.id) ? "color-blue" : "",
              }),
            },
            ...this.getCommonColumns<API_UploadFile>(),
          ];
          columns.push(this.getActionsColumn<API_UploadFile>());
          return columns;
        }

        makeRowSelection = (type: "erroredFiles" | "validFiles"): TableProps<API_UploadFile>["rowSelection"] => {
          let selectedRowKeys;
          let rowChangeFn;
          if (type === "erroredFiles") {
            selectedRowKeys = this.state.selectedErroredFileKeys;
            rowChangeFn = (keys) => this.setState({ selectedErroredFileKeys: keys });
          } else {
            selectedRowKeys = this.state.selectedValidFileKeys;
            rowChangeFn = (keys) => this.setState({ selectedValidFileKeys: keys });
          }
          return {
            selectedRowKeys: selectedRowKeys,
            onChange: rowChangeFn,
            selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
            columnWidth: 55,
          };
        };

        showChangeDescriptionModal = (files: API_UploadFile[]) => {
          const { t } = this.props;
          this.props.showModal(ChangeFileDescription, {
            modalProps: {
              title: t("Set description"),
              footer: null,
            },
            componentProps: {
              onSubmit: (newDescription) => this.handleMultiChangeDescription(files, newDescription),
            },
          });
        };

        showChangeDirectoryModal = (files: API_UploadFile[]) => {
          const { t } = this.props;
          modal.confirm({
            title: t("Change target directory"),
            content: (
              <PathDirectorySelect
                onChange={this.handleFolderIdChange}
                value={fileUploadStore.folderId}
                treeSelectProps={{
                  style: { width: "100%" },
                  placeholder: t("File path"),
                }}
              />
            ),
            onOk: () => this.handleMultiChangeDir(files as UploadedTmpFile[]),
          });
        };

        get erroredFilesActions(): DropdownActionConfig[] {
          const { t, showModal } = this.props;
          const { erroredFiles, selectedErroredFileKeys } = this.state;
          const selectedFiles = selectedErroredFileKeys
            .map((key) => erroredFiles.find((item) => item.id === +key))
            .filter(Boolean);
          const isEmptySelection = !selectedFiles.length;
          const replaceExistingFileCond = (file) =>
            file.errors && file.errors.status === "REPLACE" && file.errors.existingFile && file.errors.existingFile.id;

          const actions = [];
          if (projectsDataStore.currentProject.fileNameAssistant) {
            actions.push({
              label: `${t("Filename assistant")}...`,
              onClick: () => {
                this.handleFilenameErrors(selectedFiles);
                showModal(FilenameAssistantOverview, {
                  modalProps: {
                    className: "full-height-modal",
                    footer: null,
                    title: t("Filename assistant"),
                    width: "100%",
                    style: { top: 30, maxWidth: `calc(100vw - 80px)` },
                  },
                  componentProps: {
                    onSubmit: this.applyFilenameRulesForFiles,
                  },
                });
              },
              disabled:
                isEmptySelection ||
                !selectedFiles.every((file) => file.errors?.status === "FILENAMECONVENTION_FILENAME"),
            });
          }
          if (fileUploadStore.result.displayMissingMetadataButton) {
            actions.push({
              label: `${t("Add meta data")}...`,
              onClick: () => this.handleMetadataModalOpen(selectedFiles),
              disabled: isEmptySelection || !selectedFiles.every((file) => file.errors.status === "MISSINGMETADATUM"),
            });
            actions.push({
              label: `${t("Use previous version’s metadata")}...`,
              onClick: () => this.handleMetadataAutofill(selectedFiles, true),
              disabled: isEmptySelection || !selectedFiles.every((file) => file.errors.status === "MISSINGMETADATUM"),
            });
          }
          if (fileUploadStore.module === "DOCUMENTS") {
            actions.push({
              label: t("Change target directory"),
              onClick: () => this.showChangeDirectoryModal(selectedFiles),
              disabled: isEmptySelection || !selectedFiles.every((file) => file.changeTargetDir),
            });
            actions.push({
              label: t("Set description"),
              onClick: () => this.showChangeDescriptionModal(selectedFiles),
              disabled: isEmptySelection,
            });
          }

          actions.push({
            label: t("Replace existing file"),
            onClick: () => this.replaceExistingFiles(selectedFiles),
            disabled: isEmptySelection || !selectedFiles.every(replaceExistingFileCond),
          });
          actions.push({
            label: t("Remove"),
            onClick: () => this.handleDelete(selectedErroredFileKeys),
            disabled: isEmptySelection,
          });
          return actions;
        }

        get validFilesActions(): DropdownActionConfig[] {
          const { t } = this.props;
          const { selectedValidFileKeys, validFiles } = this.state;
          const selectedFiles = selectedValidFileKeys
            .map((key) => validFiles.find((item) => item.id === +key))
            .filter(Boolean);
          const isEmptySelection = !selectedFiles.length;
          const actions = [
            {
              label: t("Remove"),
              onClick: () => this.handleDelete(selectedValidFileKeys),
              disabled: isEmptySelection,
            },
          ];

          if (fileUploadStore.module === "DOCUMENTS") {
            actions.push({
              label: t("Set description"),
              onClick: () => this.showChangeDescriptionModal(selectedFiles),
              disabled: isEmptySelection,
            });
            actions.push({
              label: t("Change target directory"),
              onClick: () => this.showChangeDirectoryModal(selectedFiles),
              disabled: isEmptySelection || !selectedFiles.every((file) => file.changeTargetDir),
            });
          }

          return actions;
        }

        render() {
          const { t, onBack } = this.props;
          const { validFiles, erroredFiles, retryLoading, submitLoading, initialized, efPagination, vfPagination } =
            this.state;
          if (!initialized) {
            return <Skeleton paragraph={{ rows: 4 }} active={true} loading={true} />;
          }

          // For table re-render after removing file (need improvement)
          // https://pmgredesign.atlassian.net/browse/DEV-5226
          if (fileUploadStore.state === "LOADING") {
            return <Loader />;
          }

          const erroredFilesTitle = (
            <>
              {t("Not yet uploadable files")} {erroredFiles.length ? ` (${erroredFiles.length})` : null}
              <FontAwesomeIcon style={{ marginLeft: 5 }} icon={["fas", "exclamation-circle"]} color={"#ff8145"} />
            </>
          );
          const validFilesTitle = (
            <>
              {t("Already uploadable files")} {validFiles.length ? ` (${validFiles.length})` : null}
              <FontAwesomeIcon icon={["fas", "check-circle"]} style={{ marginLeft: 5 }} color={"#52c41a"} />
            </>
          );
          return (
            <div className="flex-space-between" style={{ height: "100%" }}>
              <MetadataAssistantModal
                onClose={metadataAssistantStore.toggleModalVisible}
                onEdit={this.handleMetadataEdit}
              />
              <div>
                <ReplaceFilePicker onEdit={this.handleEdit} />
                <Tabs
                  type="card"
                  items={[
                    !!erroredFiles.length && {
                      key: "invalidFiles",
                      label: erroredFilesTitle,
                      forceRender: true,
                      children: (
                        <>
                          <DetailListActions primaryActions={[]} dropdownActions={this.erroredFilesActions} />
                          <EditableCellTable<UploadedTmpFile>
                            name="fileSubmitterErrors"
                            ref={this.erroredFilesRef}
                            rowSelection={this.makeRowSelection("erroredFiles")}
                            title={() => (
                              <div className="text-center" style={{ padding: "0 10px", fontSize: 20 }}>
                                {t("Not yet uploadable files")}
                                <div style={{ float: "right" }}>
                                  <FontAwesomeIcon icon={["fas", "exclamation-circle"]} color={"#ff8145"} />
                                </div>
                              </div>
                            )}
                            className="footer-no-padding mb-2"
                            columns={this.erroredFilesColumns}
                            dataSource={erroredFiles}
                            rowKey="id"
                            bordered={true}
                            onEdit={this.handleEdit}
                            onChange={(efPagination) => this.setState({ efPagination })}
                            pagination={erroredFiles.length < 11 ? false : efPagination}
                          />
                        </>
                      ),
                    },
                    !!validFiles.length && {
                      key: "validFiles",
                      label: validFilesTitle,
                      forceRender: true,
                      children: (
                        <>
                          <DetailListActions primaryActions={[]} dropdownActions={this.validFilesActions} />
                          <EditableCellTable<API_UploadFile>
                            name="fileSubmitter"
                            ref={this.validFilesRef}
                            title={() => (
                              <div className="text-center" style={{ padding: "0 10px", fontSize: 20 }}>
                                <div>
                                  {t("Already uploadable files")}
                                  <FontAwesomeIcon
                                    icon={["fas", "check-circle"]}
                                    style={{ float: "right", marginTop: 6 }}
                                    color={"#52c41a"}
                                  />
                                </div>
                              </div>
                            )}
                            rowSelection={this.makeRowSelection("validFiles")}
                            className="footer-no-padding"
                            footer={null}
                            columns={this.validFilesColumns}
                            rowKey="id"
                            bordered={true}
                            dataSource={validFiles}
                            onEdit={this.handleEdit}
                            onChange={(vfPagination) => this.setState({ vfPagination })}
                            pagination={validFiles.length < 11 ? false : vfPagination}
                          />
                        </>
                      ),
                    },
                  ].filter(Boolean)}
                />
                <div>
                  <Row justify="space-between" className="mt-1" style={{ marginBottom: 20 }}>
                    <div>
                      <Button onClick={onBack}>{t("Back")}</Button>
                    </div>
                    <div>
                      <Button className="mr-5" onClick={() => this.handleCheck(true)} loading={retryLoading}>
                        {t("Retry")}
                      </Button>
                      <Button
                        type="primary"
                        disabled={erroredFiles.length > 0}
                        onClick={this.handleSubmit}
                        loading={submitLoading}
                      >
                        {t("Next")}
                      </Button>
                    </div>
                  </Row>
                </div>
              </div>
            </div>
          );
        }
      }
    )
  )
);
